import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '../tokens/environment.token';
import { EnvironmentService } from './environment.service';

/** The outcome of resolving a customer ID from the current URL and browser state. */
export interface CustomerResolution {
  /** The resolved customer tenant identifier. */
  customerId: string;
  /** The source from which the customer ID was resolved. Used by guards for error routing decisions. */
  source: 'override' | 'subdomain' | 'path' | 'storage';
}

/**
 * Resolves the active customer ID from the current browser URL and localStorage.
 *
 * Resolution chain (first match wins):
 *   1. `?__customerId=` query param (non-production only — developer override)
 *   2. Subdomain (e.g. `acme.qa.myemployeesportal.com` → `'acme'`)
 *   3. localStorage (previously stored customer from a prior visit or access-id entry)
 *   4. URL path segment (e.g. `myemployeesportal.com/acme` → `'acme'`)
 *
 * Note: localStorage is evaluated BEFORE path to prevent flat app-route names
 * (e.g. `/login`, `/home`) from being mistakenly resolved as customer IDs during
 * in-app navigation after the initial access-id entry. Path resolution is therefore
 * only relevant on a genuinely cold visit with no stored context.
 *
 * On every successful resolution the customer ID is written to `localStorage`,
 * overwriting any prior value.
 */
@Injectable({ providedIn: 'root' })
export class CustomerResolutionService {
  private readonly env = inject(ENVIRONMENT);
  private readonly envService = inject(EnvironmentService);
  private readonly STORAGE_KEY = 'customerId';

  /**
   * Resolves the active customer ID from the provided hostname and pathname.
   * @param hostname The current `window.location.hostname` value.
   * @param pathname The current `window.location.pathname` value.
   * @returns A {@link CustomerResolution} on success, or `null` if no customer context exists.
   */
  resolve(hostname: string, pathname: string): CustomerResolution | null {
    const candidate =
      this.parseDevOverride() ??
      this.parseSubdomain(hostname) ??
      this.parseStorage(hostname, pathname) ??
      this.parsePath(pathname);

    if (!candidate) return null;

    localStorage.setItem(this.STORAGE_KEY, candidate.customerId);
    return candidate;
  }

  /**
   * Reads the `?__customerId=` developer override from the current URL.
   * Active only in non-production builds; always returns `null` in production.
   */
  private parseDevOverride(): CustomerResolution | null {
    if (this.env.production) return null;
    const params = new URLSearchParams(globalThis.location.search);
    const id = params.get('__customerId');
    return id ? { customerId: id, source: 'override' } : null;
  }

  /**
   * Extracts the customer subdomain from the hostname after stripping the base domain
   * and any known environment subdomain labels.
   */
  private parseSubdomain(hostname: string): CustomerResolution | null {
    const host = hostname.split(':')[0]; // strip port
    const baseParts = this.envService.baseDomain.split('.');
    const hostParts = host.split('.');

    if (hostParts.length <= baseParts.length) return null;

    // Verify hostname actually ends with the configured baseDomain
    const hostSuffix = hostParts.slice(-baseParts.length);
    if (!baseParts.every((part, i) => hostSuffix[i] === part)) return null;

    const subdomainParts = hostParts.slice(0, hostParts.length - baseParts.length);

    // Remove known env labels — they are not the customer (e.g. 'qa', 'staging')
    const customerParts = subdomainParts.filter(
      (p) => !this.envService.envSubdomainLabels.includes(p)
    );

    return customerParts.length === 1
      ? { customerId: customerParts[0], source: 'subdomain' }
      : null;
  }

  /**
   * Reads a previously stored customer ID from localStorage.
   * Only uses localStorage if the customer ID is visible in the URL via subdomain or path.
   * This prevents URLs like `/home` from working with only stored context - enforcing
   * that customer ID must always be visible in the URL.
   */
  private parseStorage(hostname: string, pathname: string): CustomerResolution | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;

    // When EP is embedded as an iframe (e.g. in the MyCM/DR Wrapper app), the parent
    // delivers the customer ID via postMessage. MyCMIntegrationService writes it to
    // localStorage before APP_INITIALIZER resolves, so by the time this guard runs the
    // value is present. URL visibility is not applicable in the iframe context.
    if (this.isEmbeddedInIframe()) {
      return { customerId: stored, source: 'storage' };
    }

    // Check if we're on a customer subdomain
    const subdomainResolution = this.parseSubdomain(hostname);
    if (subdomainResolution) {
      // We're on a customer subdomain, storage is valid
      return { customerId: stored, source: 'storage' };
    }

    // Check if the path already has the customer ID under /employee-view/customers/:id
    const pathSegments = pathname.split('/').filter(Boolean);
    const hasCustomerInPath =
      (pathSegments[0] === 'employee-view' && pathSegments[1] === 'customers' && pathSegments[2] === stored) ||
      pathSegments[0] === stored; // fallback: flat/subdomain-style entry
    if (hasCustomerInPath) {
      // Customer ID is in the path, storage is valid
      return { customerId: stored, source: 'storage' };
    }

    // No visible customer identifier in URL - don't use localStorage
    return null;
  }

  /**
   * True when the app runs embedded in an iframe (e.g. the MyCM/DR wrapper), where the
   * customer ID arrives via postMessage rather than the URL. Extracted as a seam so unit
   * tests can deterministically simulate iframe vs top-level context — overriding
   * globalThis.self/top is unreliable in headless browsers (and Karma always runs in an iframe).
   */
  private isEmbeddedInIframe(): boolean {
    return globalThis.self !== globalThis.top;
  }

  /**
   * Extracts the customer ID from the URL path under `/employee-view/customers/:customerId`.
   * Applies only when there is no stored customer and no subdomain context —
   * intended for raw direct-link entry (e.g. `myemployeesportal.com/employee-view/customers/acme`).
   */
  private parsePath(pathname: string): CustomerResolution | null {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] === 'employee-view' && segments[1] === 'customers' && segments[2]) {
      return { customerId: segments[2], source: 'path' };
    }
    return null;
  }
}
